import React, { useMemo, useRef, useState } from 'react'
import { AutoComplete } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import {
  MAX_QUERY_LENGTH,
  newSearchSession,
  useAddressSearch,
} from '../../hooks/useAddressSearch'
import {
  AddressDetail,
  hasAddressComponents,
  useAddressDetail,
} from '../../hooks/useAddressDetail'
import { SearchBox, SearchHint, Suggestion } from './pin-confirm-page-styled'

type Props = {
  /** Opaque signed link token; the only credential either search phase has. */
  token?: string
  /**
   * Called with the *resolved* pick — coordinates (possibly `null`) and address
   * components — so the page can both move the pin and seed the form. This
   * component owns no pin or form state; it owns the two-phase lookup.
   */
  onSelect: (detail: AddressDetail) => void
}

/**
 * Address search for the pin-confirm map step (story sc-27850): the customer
 * types a building or business name, picks from a short list, and the page flies
 * the pin there. Sits in the bottom sheet above "Confirm pin".
 *
 * **Two phases, hidden behind one interaction.** Google Places autocomplete
 * returns predictions with a `place_id` and no geometry, so tapping a suggestion
 * is not the end of the lookup: this component then resolves that place id to a
 * point and address components (`useAddressDetail`) before telling the page
 * anything. The page therefore still hears about a pick exactly once, already
 * resolved — the two-phase-ness is contained here.
 *
 * An `AutoComplete` rather than a `Select`, for two reasons: the options are
 * suggestions the customer may ignore entirely (dragging the pin stays a first
 * class way to answer), and nothing is submitted from this field — picking an
 * option is an action, not a value. So this component keeps its own text state
 * and never participates in the address form.
 *
 * Nothing here can fail visibly. A dead or empty search, and a pick that cannot
 * be resolved, are all quiet `role="status"` hints, never errors, and none of
 * them stops the customer dragging the pin and confirming (AC 4).
 */
export default function AddressSearchInput({ token, onSelect }: Props) {
  // The text in the box. Kept local: the page only ever hears about a pick.
  const [text, setText] = useState('')
  // Set the moment a suggestion is picked, so putting that suggestion's label
  // into the box doesn't immediately fire a fresh search for its own label.
  // Cleared as soon as the customer edits the text again.
  const [picked, setPicked] = useState(false)
  /**
   * Google's autocomplete session token, threaded through every suggest call and
   * the detail call that closes it so the whole search bills as one session
   * instead of one lookup per keystroke. Replaced once a pick has been resolved:
   * that session is spent, and the next search is a new one.
   */
  const [session, setSession] = useState(newSearchSession)
  /** Set when a pick resolved to neither a point nor a single component. */
  const [pickFailed, setPickFailed] = useState(false)
  /** Set when a pick gave us components but no point to fly to. */
  const [pickHadNoPoint, setPickHadNoPoint] = useState(false)
  /**
   * The place id of the pick the customer is currently waiting on, or `null` once
   * they have moved on (edited the text, cleared the box). Phase 2 is awaited, so
   * a detail answer can land after its pick stopped being the one that matters —
   * a second tap inside the same suggestion list, or simply typing on while the
   * first lookup is in flight. Applying such an answer would fly the pin to an
   * abandoned place and re-show a hint about it over an unrelated query, which is
   * the same out-of-order-response class as the fly race this page already had.
   * So a resolved detail is applied only while this still names its own pick.
   */
  const awaitedPlaceId = useRef<string | null>(null)

  const { suggestions, isSearching, hasSearchableQuery } = useAddressSearch(
    token,
    picked ? '' : text,
    session
  )
  const { fetchAddressDetail, isResolving } = useAddressDetail()

  /**
   * Each option's `value` is its **place id**, not its label.
   *
   * This is load-bearing. Places returns the Burj Khalifa tower and the district
   * of the same name in one response, so labels are not unique — and rc-select
   * resolves a clicked option back to its data *by value*, keeping the last
   * option for a duplicated one. With labels as values, tapping the tower
   * selected the district: the pin flew to the wrong place and Building name
   * came back empty, reproducing the very bug this change exists to fix.
   *
   * The place id is unique by construction, so the row the customer tapped is
   * the row that resolves. What they *see* comes from `label` (a node, so the
   * secondary line renders under the name), and the text that lands in the box
   * is set explicitly in `handleSelect` — never the raw place id.
   */
  const options = useMemo(
    () =>
      suggestions.map((suggestion) => ({
        value: suggestion.placeId,
        label: (
          <Suggestion>
            <span className="suggestion-label">{suggestion.label}</span>
            {suggestion.secondary ? (
              <span className="suggestion-secondary">
                {suggestion.secondary}
              </span>
            ) : null}
          </Suggestion>
        ),
      })),
    [suggestions]
  )

  const handleSelect = async (placeId: string) => {
    const suggestion = suggestions.find((one) => one.placeId === placeId)

    if (!suggestion || !token) {
      return
    }

    // The human label goes in the box, never the place id that identified it.
    // `picked` stops that label immediately becoming the next search query.
    setText(suggestion.label)
    setPicked(true)
    setPickFailed(false)
    setPickHadNoPoint(false)
    awaitedPlaceId.current = suggestion.placeId

    // Never rejects — see `useAddressDetail`.
    const detail = await fetchAddressDetail({
      token,
      placeId: suggestion.placeId,
      session,
    })

    // Replaced whether or not this answer is still wanted: Google spends an
    // autocomplete session the moment a Place Details call uses its token, so a
    // superseded pick has spent it just as thoroughly as the current one.
    setSession(newSearchSession())

    if (awaitedPlaceId.current !== suggestion.placeId) {
      // Superseded — a later pick, or the customer typing on. Say nothing and
      // touch nothing: whatever replaced this pick owns the box and the pin.
      return
    }

    awaitedPlaceId.current = null

    const knowsSomething =
      !!detail.coordinates || hasAddressComponents(detail.address)

    if (!knowsSomething) {
      // Nothing came back at all. Say so quietly and leave the pin alone —
      // handing the page an empty detail would be a no-op anyway, but staying
      // silent would make the tap look simply broken.
      setPickFailed(true)
      return
    }

    // Components with no point are still worth having: they fill the form even
    // though the pin cannot move, so tell the customer to place it themselves.
    setPickHadNoPoint(!detail.coordinates)
    onSelect(detail)
  }

  // "We looked and found nothing" — shown only once the debounce has settled on
  // a searchable query, so it never nags a customer who is still typing.
  // A hint, deliberately not an error: zero results must not block the flow.
  const showNoMatch =
    hasSearchableQuery && !isSearching && suggestions.length === 0

  // One hint slot, most-specific state first. Every one of these is advisory:
  // `role="status"` (a polite live region) rather than `role="alert"`, quiet
  // styling, and never a reason the customer cannot carry on by hand.
  let hint: { testId: string; message: string } | null = null
  if (isResolving) {
    hint = {
      testId: 'address-search-resolving',
      message: 'Finding that address…',
    }
  } else if (pickFailed) {
    hint = {
      testId: 'address-search-pick-failed',
      message:
        'We couldn’t load that address — you can still drag the pin to your door.',
    }
  } else if (pickHadNoPoint) {
    hint = {
      testId: 'address-search-no-point',
      message:
        'We’ve filled in that address — please drag the pin to your exact door.',
    }
  } else if (showNoMatch) {
    hint = {
      testId: 'address-search-no-match',
      message:
        'No matching address found — you can still drag the pin to your door.',
    }
  }

  return (
    <SearchBox>
      <SearchOutlined className="search-icon" />
      <AutoComplete
        value={text}
        placeholder="Search for a building, business or area"
        allowClear
        maxLength={MAX_QUERY_LENGTH}
        aria-label="Search for your delivery address"
        options={options}
        onChange={(value: string) => {
          // On a selection rc-select echoes the chosen option's `value` — a place
          // id — through onChange as well. That is not something the customer
          // typed, so drop it and let `onSelect` put the human label in the box.
          // Checked explicitly rather than relying on onChange firing before
          // onSelect, so the text in the box never depends on handler ordering.
          if (suggestions.some((one) => one.placeId === value)) {
            return
          }

          setText(value ?? '')
          setPicked(false)
          // Editing the text retracts the commentary about the previous pick,
          // and abandons a pick still being resolved: its answer must not land
          // on top of the search the customer has moved on to.
          setPickFailed(false)
          setPickHadNoPoint(false)
          awaitedPlaceId.current = null
        }}
        onSelect={handleSelect}
        // The backend has already ranked and trimmed these to five; filtering
        // again client-side against the raw input would hide good matches whose
        // label doesn't literally contain what was typed ("Quiqup Delivery LLC"
        // is ranked for "quiqup" by Places, not by substring).
        filterOption={false}
        // No "not found" panel inside the dropdown: the hint below the box says
        // it in place, without an empty popover covering the map.
        notFoundContent={null}
        data-testid="address-search-input"
      />
      {hint ? (
        // `role="status"`, not `role="alert"`: with `notFoundContent={null}` the
        // dropdown never opens on zero results, so without this a screen-reader
        // user is told nothing at all. Still a hint, never an error — the styling
        // and wording stay quiet.
        <SearchHint role="status" data-testid={hint.testId}>
          {hint.message}
        </SearchHint>
      ) : null}
    </SearchBox>
  )
}
